import React, { ReactElement, ReactNode, useCallback, useRef } from 'react';
import { Col, Form, Grid, InputGroup, Row, Text } from 'rsuite';
import { FORM_INPUT_TYPES, INPUT_TYPE } from '../../../common/enums';
import { CheckType } from 'schema-typed';
import { FormControlBaseProps } from 'rsuite/esm/internals/types';
import { BaseTextInputWithErrorStatus } from './types';
import { CustomTextInput } from './CustomTextInput';
import FormFieldErrorIcon from '../FormFieldErrorIcon';

export interface FormTextFieldProps extends BaseTextInputWithErrorStatus {
  'data-cy'?: string;
  hasError?: boolean; // Error Message Should be enough to know if we have an error. This prop should be deprecated.
  hideErrorIcon?: boolean;
  boldLabel?: boolean;
  bottomMarginOverrideClass?: string;
  inputWidth?: string;
  label?: string | ReactNode;
  labelClass?: string;
  ariaLabel?: string;
  required?: boolean;
  subtext?: () => ReactElement;
  subtitle?: () => ReactElement;
  validationRule?: CheckType<unknown, any> | null; // This prop should be deprecated in favor of rule from FormControlProps
  disabled?: boolean;
  inputFieldType?: any;
}

export const FormTextField = ({
  ariaLabel,
  boldLabel,
  bottomMarginOverrideClass,
  disabled,
  errorMessage,
  hasError,
  hideErrorIcon,
  inputFieldType = INPUT_TYPE.TEXT,
  inputGroupAddOnStart = undefined,
  inputMode,
  inputWidth,
  label,
  labelClass,
  name = '',
  preventHtmlValidation = false,
  required,
  subtext = () => <></>,
  subtitle = () => <></>,
  trimEndOnChange,
  trimStartOnChange,
  type = FORM_INPUT_TYPES.TEXT,
  useGridSizing = false,
  validationRule,
  ...rest
}: FormTextFieldProps): ReactElement => {
  const errorId = hasError && name ? `${name}-error` : undefined;
  const inputId = name
    ? name
        .replace(/[[\].]/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '')
    : undefined;

  const hasErrorRef = useRef(hasError);
  hasErrorRef.current = hasError;
  const errorIdRef = useRef(errorId);
  errorIdRef.current = errorId;

  // A visible label already provides the accessible name via htmlFor/id, so
  // ariaLabel is only wired through when there is no visible label — never
  // let it override the visible label
  const resolvedAriaLabel = label ? undefined : ariaLabel;

  const CustomInputTextInputWithProps = useCallback(
    (props: FormControlBaseProps) => {
      return (
        <CustomTextInput
          {...props}
          id={inputId}
          trimStartOnChange={trimStartOnChange}
          trimEndOnChange={trimEndOnChange}
          inputGroupAddOnStart={inputGroupAddOnStart}
          type={type}
          inputFieldType={inputFieldType}
          inputMode={inputMode}
          preventHtmlValidation={preventHtmlValidation}
          name={props.name || ''}
          disabled={disabled}
          ariaInvalid={hasErrorRef.current ? true : undefined}
          ariaDescribedby={errorIdRef.current}
          ariaLabel={resolvedAriaLabel}
        />
      );
    },
    [
      inputId,
      trimStartOnChange,
      trimEndOnChange,
      inputGroupAddOnStart,
      preventHtmlValidation,
      inputFieldType,
      type,
      inputMode,
      disabled,
      resolvedAriaLabel,
    ]
  );

  return (
    <Grid
      className={bottomMarginOverrideClass}
      style={{ paddingLeft: 0, paddingRight: 0 }}
      fluid
    >
      <Row>
        <Col xs={24}>
          {label && (
            <Form.ControlLabel
              htmlFor={inputId}
              className={labelClass}
            >
              <Text
                as="h5"
                className={boldLabel ? 'text-bold' : ''}
              >
                {label}
                {required && ' *'}
              </Text>
            </Form.ControlLabel>
          )}
        </Col>
      </Row>
      <Row>
        <Col>{subtitle()}</Col>
      </Row>
      <Row>
        <Col
          {...(useGridSizing
            ? {
                xs: 24,
                md: inputFieldType === 'textarea' ? 24 : 12,
                lg: inputFieldType === 'textarea' ? 24 : 12,
                xl: inputFieldType === 'textarea' ? 12 : 6,
              }
            : { xs: 24 })}
        >
          <InputGroup
            data-testid={name}
            data-cy={rest['data-cy']}
          >
            {inputGroupAddOnStart && (
              <InputGroup.Addon style={{ backgroundColor: 'white' }}>
                {inputGroupAddOnStart}
              </InputGroup.Addon>
            )}
            <Form.Control
              style={{ width: inputWidth ?? '100%' }}
              className={disabled ? 'disabled-input' : undefined}
              name={name}
              rule={validationRule}
              accepter={CustomInputTextInputWithProps}
              disabled={disabled}
              value={rest.value}
              {...rest}
            />
            {!hideErrorIcon && hasError && (
              <InputGroup.Addon style={{ backgroundColor: 'white' }}>
                <FormFieldErrorIcon errorMessage={errorMessage} />
              </InputGroup.Addon>
            )}
          </InputGroup>
          {hasError && errorMessage && (
            <span
              id={`${name}-error`}
              className="text-error"
            >
              {errorMessage}
            </span>
          )}
          {subtext()}
        </Col>
      </Row>
    </Grid>
  );
};
